import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Stack,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Typography,
  IconButton,
  Alert,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Info,
  Check,
  Sparkles,
  Folder,
  FileText,
  Heart,
  Tag,
  X,
  Mail,
  CreditCard,
  XCircle,
  Copy,
  Crown,
} from 'lucide-mui';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../reducers';
import { USER_PACKAGE } from '../../plan/userPackage';
import { config } from '../../../../config';
import { getChurnkeyData } from '../../../services/subscriptionServices';
import logger from '../../../lib/logger';
import UpsellButton from '../../dashboard/toolbar/UpsellButton';
import UpgradeButton from '../../dashboard/toolbar/UpgradeButton';
import planFeatures from '../../plan/upgrade/upgradeView/featureLimits';
import { planPricesPerMonth } from '../../../constants/prices';
import { ToggleSetting, SettingsSection } from '../common';
import {
  trackCancelPlanModal,
  trackCancelPlanButton,
} from '../../plan/planSlice';

const Billing = () => {
  const dispatch = useDispatch();
  const { plan, team, library, settings } = useSelector(
    (state: RootState) => state
  );
  const { stripePaymentUpdateUrl } = config;
  const { teamId } = team.selectedTeam || {};
  const { package: teamPackage, term, price: currentPrice } =
    plan.subscription || {};
  const { providerId } = plan.subscription || {};
  const { usage, packageProperties } = plan;
  const isFreeUser: boolean = teamPackage === USER_PACKAGE.FREE_LOGGEDIN;
  const [cancelFailed, setCancelFailed] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [isHeartPulsing, setIsHeartPulsing] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const currentPricePerMonth =
    term === 'monthly' ? currentPrice : currentPrice / 12;

  const usageFeatures = [
    {
      key: 'askAICount',
      label: 'AI Credits Used',
      icon: <Sparkles />,
      color: theme.palette.warning.main,
      value: usage.askAICount ?? 0,
      max: packageProperties.askAICount || 0,
    },
    {
      key: 'transcriptionCount',
      label: 'Transcriptions',
      icon: <FileText />,
      color: theme.palette.primary.main,
      value: usage.transcriptionCount ?? 0,
      max: packageProperties.transcriptionCount || 0,
    },
    {
      key: 'librarySize',
      label: 'Access to Files in Library',
      icon: <Folder />,
      color: theme.palette.secondary.main,
      value: library.files.data.length ?? 0,
      max: packageProperties.librarySize || 0,
    },
  ]
    .filter((feature) => feature.value > 0)
    .sort((a, b) => b.value - a.value);

  const handleOpenCancelModal = () => {
    dispatch(trackCancelPlanModal({ modal: 'cancel-plan' }));
    setOpenCancelModal(true);
  };

  const handleCancelPlan = async () => {
    dispatch(trackCancelPlanButton({ button: 'cancel-subscription' }));
    setCancelFailed(false);
    const { churnkeyAppId, churunkeyMode } = config;
    if (!teamId) {
      logger.error('teamId is not available');
      setCancelFailed(true);
      return;
    }
    const { customerId, subscriptionId, authHash } =
      (await getChurnkeyData({
        teamId,
      })) || {};
    if (!customerId || !subscriptionId || !authHash) {
      logger.error('churnkey data is not available');
      setCancelFailed(true);
      return;
    }
    try {
      window.churnkey.init('show', {
        customerId,
        subscriptionId,
        authHash,
        appId: churnkeyAppId,
        mode: churunkeyMode,
        provider: 'stripe',
      });
      setOpenCancelModal(false);
    } catch (error) {
      logger.error('could not cancel the plan. error: ', error);
      setCancelFailed(true);
    }
  };

  const formatPackageName = (packageName: string) => {
    if (packageName === USER_PACKAGE.FREE_LOGGEDIN) {
      return 'Free';
    }
    return (
      packageName.charAt(0).toUpperCase() + packageName.slice(1).toLowerCase()
    );
  };

  const getCurrentPlanFeatures = () => {
    if (!teamPackage) return [];
    const features = planFeatures[teamPackage] || {};
    return Object.entries(features)
      .sort(([, a], [, b]) => a.ranking - b.ranking)
      .map(([, feature]) => feature)
      .slice(0, 13);
  };

  const showDealNotice = () => {
    if (!teamPackage || isFreeUser) return false;
    const currentPlan = teamPackage as keyof typeof planPricesPerMonth;
    const currentMonthlyPrice = planPricesPerMonth[currentPlan]?.monthly;
    return currentPricePerMonth < currentMonthlyPrice;
  };

  const handleKeepPlan = () => {
    setIsHeartPulsing(true);
    setTimeout(() => {
      setOpenCancelModal(false);
      setIsHeartPulsing(false);
    }, 1000);
  };

  const getPriceDisplay = () => {
    if (!currentPricePerMonth) return '';
    if (term === 'monthly') return ` at $${currentPricePerMonth} per month`;
    return ` at $${currentPrice} per year (equivalent to $${currentPricePerMonth} per month)`;
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        height: '100%',
        overflow: 'auto',
        bgcolor: 'transparent',
        px: 3,
        pt: 1,
        pb: 3,
        '& > *:not(:last-child)': {
          mb: 3,
        },
      }}
    >
      <SettingsSection title="Billing and Plans">
        <ToggleSetting
          checked={false}
          onChange={() => {}}
          label="Current Plan"
          description={`You're currently subscribed to the ${
            isFreeUser
              ? 'Free plan'
              : `${formatPackageName(teamPackage)} ${
                  term?.charAt(0).toUpperCase() + term?.slice(1)
                } plan${getPriceDisplay()}`
          }`}
          icon={<Crown />}
        >
          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                p: 2,
              }}
            >
              {getCurrentPlanFeatures().map((feature, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    py: 1,
                    px: 2,
                    '&:not(:last-child)': {
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    },
                  }}
                >
                  <Check
                    sx={{
                      mr: 2,
                      color: 'success.main',
                      fontSize: 20,
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {feature.title}
                    </Typography>
                    {feature.description && (
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </ToggleSetting>

        {isFreeUser ? (
          <ToggleSetting
            checked={false}
            onChange={() => {}}
            label="Upgrade Plan"
            description="Get access to premium features by upgrading your plan"
            icon={<Sparkles />}
            rightElement={<UpgradeButton />}
          />
        ) : (
          <ToggleSetting
            checked={false}
            onChange={() => {}}
            label="Available Upgrades"
            description="Explore additional features and higher usage limits"
            icon={<Tag />}
            rightElement={<UpsellButton />}
          />
        )}

        {!isFreeUser && (
          <>
            <ToggleSetting
              checked={false}
              onChange={() => {
                window.open(
                  `${stripePaymentUpdateUrl}?prefilled_email=${settings.user.email}`,
                  '_blank',
                  'noopener,noreferrer'
                );
              }}
              label="Payment Details"
              description="Update your payment method or billing information"
              icon={<CreditCard />}
              rightElement={
                <Button
                  variant="outlined"
                  color="secondary"
                  href={`${stripePaymentUpdateUrl}?prefilled_email=${settings.user.email}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Update Payment Details
                </Button>
              }
            />

            {providerId && (
              <ToggleSetting
                checked={false}
                onChange={() => {}}
                label="Subscription ID"
                description="Use this ID when contacting support about billing questions"
                icon={<Info />}
                rightElement={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {providerId}
                    </Typography>
                    <Tooltip title="Copy Subscription ID" arrow>
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigator.clipboard.writeText(providerId)
                        }
                        sx={{
                          color: theme.palette.text.secondary,
                          '&:hover': {
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        <Copy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              />
            )}
          </>
        )}
      </SettingsSection>

      {!isFreeUser && (
        <SettingsSection title="Danger Area">
          <ToggleSetting
            checked={false}
            onChange={() => handleOpenCancelModal()}
            label="Cancel Plan"
            description="Cancel your current subscription - this action cannot be undone"
            icon={<XCircle />}
            rightElement={
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleOpenCancelModal()}
              >
                Cancel Plan
              </Button>
            }
          />
        </SettingsSection>
      )}

      <Dialog
        open={openCancelModal}
        onClose={() => setOpenCancelModal(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: 2,
            m: isMobile ? 0 : 2,
          },
        }}
      >
        <DialogContent sx={{ p: isMobile ? 2 : 4 }}>
          <IconButton
            onClick={() => setOpenCancelModal(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <X />
          </IconButton>

          <Heart
            sx={{
              fontSize: 80,
              color: theme.palette.error.main,
              display: 'block',
              margin: '0 auto',
              mb: 3,
              animation: isHeartPulsing ? 'pulse 1s ease-in-out' : 'none',
              '@keyframes pulse': {
                '0%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.2)' },
                '100%': { transform: 'scale(1)' },
              },
            }}
          />

          <Typography variant="h4" sx={{ textAlign: 'center', mb: 2 }}>
            Stay with us!
          </Typography>

          {showDealNotice() && (
            <Alert
              severity="success"
              sx={{
                mb: 4,
                background: alpha(theme.palette.success.main, 0.1),
                '& .MuiAlert-icon': {
                  fontSize: '2rem',
                },
              }}
            >
              <Typography variant="h6" sx={{ mb: 1 }}>
                You have a great deal!
              </Typography>
              You pay <strong>${currentPricePerMonth}/month</strong>, while
              current price is{' '}
              <strong>
                $
                {
                  planPricesPerMonth[
                    teamPackage as keyof typeof planPricesPerMonth
                  ]?.monthly
                }
                /month
              </strong>
              .
            </Alert>
          )}

          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              mb: 4,
              color: theme.palette.text.secondary,
              maxWidth: '500px',
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            We love having you as part of our community! Your current plan gives
            you premium features that help you work smarter and faster. Before
            you go, let us know if there&apos;s anything we can do to improve
            your experience - we&apos;re always here to help.
          </Typography>

          {usageFeatures.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                mb: 4,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Benefits You&apos;ll Lose
                </Typography>
                <List sx={{ p: 0 }}>
                  {usageFeatures.map((feature, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        background: alpha(feature.color, 0.1),
                        borderRadius: 1,
                        mb: 1,
                        px: isMobile ? 1 : 2,
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            bgcolor: feature.color,
                            transform: 'scale(1.1)',
                            width: isMobile ? 32 : 40,
                            height: isMobile ? 32 : 40,
                          }}
                        >
                          {feature.icon}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography
                            variant={isMobile ? 'body1' : 'subtitle1'}
                            sx={{ fontWeight: 500 }}
                          >
                            {feature.label}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant={isMobile ? 'subtitle1' : 'h6'}
                            sx={{ color: 'text.primary' }}
                          >
                            {feature.value}
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{
                                ml: 1,
                                color: 'text.primary',
                              }}
                            >
                              {feature.key === 'librarySize'
                                ? 'Total'
                                : 'this month'}
                            </Typography>
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          )}

          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={3}
            sx={{
              justifyContent: 'center',
              mb: 3,
              '& .MuiButton-root': {
                minWidth: isMobile ? '100%' : 200,
                whiteSpace: 'nowrap',
              },
            }}
          >
            <Button
              variant="contained"
              color="success"
              startIcon={<Heart sx={{ color: 'inherit' }} />}
              onClick={handleKeepPlan}
              size={isMobile ? 'medium' : 'large'}
              sx={{
                px: isMobile ? 2 : 4,
                py: isMobile ? 1 : 1.5,
                fontSize: isMobile ? '1rem' : '1.1rem',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'scale(1.02)',
                  '& .MuiSvgIcon-root': {
                    color: 'inherit',
                  },
                },
                order: 1,
              }}
            >
              Keep My Plan
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={handleCancelPlan}
              size={isMobile ? 'medium' : 'large'}
              sx={{
                px: isMobile ? 2 : 4,
                py: isMobile ? 1 : 1.5,
                fontSize: isMobile ? '1rem' : '1.1rem',
                borderWidth: 1,
                order: 2,
              }}
            >
              Cancel Subscription
            </Button>
          </Stack>

          {cancelFailed && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                fontSize: isMobile ? '0.875rem' : '1rem',
              }}
            >
              Please disable any content blockers and try again.
            </Alert>
          )}

          <Box sx={{ textAlign: 'center', display: 'none' }}>
            <Button
              variant="outlined"
              color="secondary"
              href="mailto:support@screenapp.io"
              startIcon={<Mail />}
              size="small"
              sx={{
                borderRadius: 4,
                fontSize: isMobile ? '0.75rem' : '0.875rem',
              }}
            >
              Contact our support team
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Billing;
